import { c as _c } from "react/compiler-runtime";
import { Badge, BadgeProps, Box, BoxProps, Carousel, Heading, HeadingProps, SimpleGrid, SimpleGridProps, StackProps, Text, TextProps, VStack } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import React from 'react';
export interface GenericSocialProofItem {
  /** Product name shown in the top-left badge. */
  productName: string;
  /** Large company / social-proof name at the bottom of the card. */
  companyName: string;
  /** Small uppercase-style label under the company name (e.g. "TOTAL RAISED"). */
  eyebrow: string;
  /** Value line under the eyebrow (e.g. "$45 Million"). */
  value: string;
  /** Optional per-card color palette override. */
  colorPalette?: BoxProps['colorPalette'];
  badgeProps?: BadgeProps;
  companyNameProps?: HeadingProps;
  eyebrowProps?: TextProps;
  valueProps?: TextProps;
  cardProps?: StackProps;
}
export interface GenericSocialProofProps {
  /** Optional when nested inside another section (e.g. GenericSection). */
  heading?: string;
  items: GenericSocialProofItem[];
  /** Section background. Defaults to `colorPalette.panel`. */
  background?: BoxProps['background'];
  colorPalette?: BoxProps['colorPalette'];
  /** Column count for the desktop grid. Defaults to `Math.min(items.length, 4)`. */
  columns?: SimpleGridProps['columns'];
  containerProps?: BoxProps;
  headingProps?: HeadingProps;
  gridProps?: SimpleGridProps;
  carouselProps?: Omit<Carousel.RootProps, 'slideCount'>;
  /** Shared defaults applied to every card (per-item `cardProps` win). */
  cardProps?: StackProps;
  badgeProps?: BadgeProps;
  companyNameProps?: HeadingProps;
  eyebrowProps?: TextProps;
  valueProps?: TextProps;
}
const SocialProofCard: React.FC<{
  item: GenericSocialProofItem;
  cardProps?: StackProps;
  badgeProps?: BadgeProps;
  companyNameProps?: HeadingProps;
  eyebrowProps?: TextProps;
  valueProps?: TextProps;
  padding?: StackProps['padding'];
  companyNameSize?: HeadingProps['size'];
}> = t0 => {
  const $ = _c(30);
  const {
    item,
    cardProps,
    badgeProps,
    companyNameProps,
    eyebrowProps,
    valueProps,
    padding,
    companyNameSize
  } = t0;
  let t1;
  if ($[0] !== badgeProps || $[1] !== item.badgeProps || $[2] !== item.productName) {
    t1 = <Badge size="sm" variant="subtle" {...badgeProps} {...item.badgeProps}>{item.productName}</Badge>;
    $[0] = badgeProps;
    $[1] = item.badgeProps;
    $[2] = item.productName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== companyNameProps || $[5] !== companyNameSize || $[6] !== item.companyName || $[7] !== item.companyNameProps) {
    t2 = <Heading as="h3" lineHeight="shorter" size={companyNameSize} {...companyNameProps} {...item.companyNameProps}>{item.companyName}</Heading>;
    $[4] = companyNameProps;
    $[5] = companyNameSize;
    $[6] = item.companyName;
    $[7] = item.companyNameProps;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== eyebrowProps || $[10] !== item.eyebrow || $[11] !== item.eyebrowProps) {
    t3 = <Text color="colorPalette.fg/50" letterSpacing="widest" textStyle="labelXs" textTransform="uppercase" {...eyebrowProps} {...item.eyebrowProps}>{item.eyebrow}</Text>;
    $[9] = eyebrowProps;
    $[10] = item.eyebrow;
    $[11] = item.eyebrowProps;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== item.value || $[14] !== item.valueProps || $[15] !== valueProps) {
    t4 = <Text color="colorPalette.fg" fontSize="lg" fontWeight="semibold" {...valueProps} {...item.valueProps}>{item.value}</Text>;
    $[13] = item.value;
    $[14] = item.valueProps;
    $[15] = valueProps;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <VStack align="flex-start" gap={0} width="full">{t3}{t4}</VStack>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t2 || $[21] !== t5) {
    t6 = <VStack align="flex-start" gap={2} width="full">{t2}{t5}</VStack>;
    $[20] = t2;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== cardProps || $[24] !== item.cardProps || $[25] !== item.colorPalette || $[26] !== padding || $[27] !== t1 || $[28] !== t6) {
    t7 = <VStack align="flex-start" background="colorPalette.card/50" borderRadius="2xl" colorPalette={item.colorPalette} gap={6} height="full" justify="space-between" minHeight="240px" padding={padding} width="full" {...cardProps} {...item.cardProps}>{t1}{t6}</VStack>;
    $[23] = cardProps;
    $[24] = item.cardProps;
    $[25] = item.colorPalette;
    $[26] = padding;
    $[27] = t1;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
};

/**
 * GenericSocialProof – A full-page landing block with an optional centered heading
 * and a row of social-proof cards. Uses a Carousel on mobile/tablet and a grid on desktop.
 * Omit `heading` when nested inside another section (e.g. GenericSection).
 */
export const GenericSocialProof: React.FC<GenericSocialProofProps> = t0 => {
  const $ = _c(43);
  const {
    heading,
    items,
    background: t1,
    colorPalette,
    columns,
    containerProps,
    headingProps,
    gridProps,
    carouselProps,
    cardProps,
    badgeProps,
    companyNameProps,
    eyebrowProps,
    valueProps
  } = t0;
  const background = t1 === undefined ? "colorPalette.panel" : t1;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t2;
  if ($[0] !== getBreakpointValue) {
    t2 = getBreakpointValue([true, true, true, false, false]);
    $[0] = getBreakpointValue;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const useCarousel = t2;
  const resolvedColumns = columns ?? Math.min(items.length, 4);
  let t3;
  if ($[2] !== getBreakpointValue) {
    t3 = getBreakpointValue([6, 8, 10]);
    $[2] = getBreakpointValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const cardPadding = t3;
  let t4;
  if ($[4] !== getBreakpointValue) {
    t4 = getBreakpointValue(["2xl", "3xl", "4xl"]);
    $[4] = getBreakpointValue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const companyNameSize = t4;
  let t5;
  if ($[6] !== badgeProps || $[7] !== cardPadding || $[8] !== cardProps || $[9] !== companyNameProps || $[10] !== companyNameSize || $[11] !== eyebrowProps || $[12] !== valueProps) {
    t5 = {
      cardProps,
      badgeProps,
      companyNameProps,
      eyebrowProps,
      valueProps,
      padding: cardPadding,
      companyNameSize
    };
    $[6] = badgeProps;
    $[7] = cardPadding;
    $[8] = cardProps;
    $[9] = companyNameProps;
    $[10] = companyNameSize;
    $[11] = eyebrowProps;
    $[12] = valueProps;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const sharedCardProps = t5;
  let t6;
  if ($[14] !== getBreakpointValue) {
    t6 = getBreakpointValue([4, 6, 8]);
    $[14] = getBreakpointValue;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== getBreakpointValue) {
    t7 = getBreakpointValue([12, 12, 14, 14, 20, 24]);
    $[16] = getBreakpointValue;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== getBreakpointValue) {
    t8 = getBreakpointValue([8, 8, 10, 12]);
    $[18] = getBreakpointValue;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== getBreakpointValue || $[21] !== heading || $[22] !== headingProps) {
    t9 = heading && <Heading as="h2" size={getBreakpointValue(["2xl", "3xl", "4xl", "4xl", "5xl"])} textAlign="center" {...headingProps}>{heading}</Heading>;
    $[20] = getBreakpointValue;
    $[21] = heading;
    $[22] = headingProps;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== carouselProps || $[25] !== getBreakpointValue || $[26] !== gridProps || $[27] !== items || $[28] !== resolvedColumns || $[29] !== sharedCardProps || $[30] !== useCarousel) {
    t10 = useCarousel ? <Carousel.Root slideCount={items.length} width="full" {...carouselProps}><Carousel.ItemGroup>{items.map((item, index) => <Carousel.Item key={`${item.companyName}-${index}`} index={index} paddingX={2}><SocialProofCard item={item} {...sharedCardProps} /></Carousel.Item>)}</Carousel.ItemGroup><Carousel.Control justifyContent="center"><Carousel.Indicators /></Carousel.Control></Carousel.Root> : <SimpleGrid columns={resolvedColumns} gap={getBreakpointValue([4, 4, 6])} width="full" {...gridProps}>{items.map((item_0, index_0) => <SocialProofCard key={`${item_0.companyName}-${index_0}`} item={item_0} {...sharedCardProps} />)}</SimpleGrid>;
    $[24] = carouselProps;
    $[25] = getBreakpointValue;
    $[26] = gridProps;
    $[27] = items;
    $[28] = resolvedColumns;
    $[29] = sharedCardProps;
    $[30] = useCarousel;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t8 || $[34] !== t9) {
    t11 = <VStack align="center" gap={t8} marginX="auto" maxWidth="container.xl" width="full">{t9}{t10}</VStack>;
    $[32] = t10;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== background || $[37] !== colorPalette || $[38] !== containerProps || $[39] !== t11 || $[40] !== t6 || $[41] !== t7) {
    t12 = <Box as="section" background={background} colorPalette={colorPalette} paddingX={t6} paddingY={t7} width="full" {...containerProps}>{t11}</Box>;
    $[36] = background;
    $[37] = colorPalette;
    $[38] = containerProps;
    $[39] = t11;
    $[40] = t6;
    $[41] = t7;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
};
export default GenericSocialProof;